import { useAppSelector } from '../../../app/hooks'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../@types/types'

export interface SegmentationState {
  selectedSegmentation: SegmentationType | null
  selectedIntent: TransactionType | 'RENT_LIMITED' | null
  isShortTermAccommodation: boolean
  showBusinessCategories: boolean
  selectedPropertyTypes: RealEstateCategory[]
  validationErrors: {
    identity?: string[]
    intent?: string[]
    propertyTypes?: string[]
  }
  // Computed UI state
  selectedTransaction: TransactionType | 'SHORT_TERM_ACCOMMODATION' | undefined
  selectedRealEstate: 'BUSINESS' | RealEstateCategory | undefined
  selectedBusinessType: RealEstateCategory | undefined
}

/**
 * Custom hook to access segmentation state from Redux
 * Consolidates all segmentation-related selectors and computed values
 */
export const useSegmentationState = (): SegmentationState => {
  // Raw Redux state
  const selectedSegmentation = useAppSelector((state) => state.segmentationFeature.selectedIdentity)
  const selectedIntent = useAppSelector((state) => state.segmentationFeature.selectedIntent)
  const isShortTermAccommodation = useAppSelector((state) => state.segmentationFeature.isShortTermAccommodation)
  const showBusinessCategories = useAppSelector((state) => state.segmentationFeature.showBusinessCategories)
  const selectedPropertyTypes = useAppSelector((state) => state.segmentationFeature.selectedPropertyTypes)
  const validationErrors = useAppSelector((state) => state.segmentationFeature.validationErrors)

  // Computed UI state: map Redux intent to UI transaction type
  // RENT_LIMITED from Redux maps to RENT for UI purposes
  const selectedTransaction: TransactionType | 'SHORT_TERM_ACCOMMODATION' | undefined = isShortTermAccommodation
    ? 'SHORT_TERM_ACCOMMODATION'
    : selectedIntent === 'RENT_LIMITED'
      ? TransactionType.RENT
      : ((selectedIntent as TransactionType | undefined) ?? undefined)

  // Computed: selected real estate for UI display
  const selectedRealEstate: 'BUSINESS' | RealEstateCategory | undefined = showBusinessCategories
    ? 'BUSINESS'
    : selectedPropertyTypes[0]

  // Computed: selected business type (if applicable)
  const selectedBusinessType = showBusinessCategories ? selectedPropertyTypes[0] : undefined

  return {
    selectedSegmentation,
    selectedIntent,
    isShortTermAccommodation,
    showBusinessCategories,
    selectedPropertyTypes,
    validationErrors,
    selectedTransaction,
    selectedRealEstate,
    selectedBusinessType,
  }
}
